import { useState, useEffect } from 'react';
import AdminLayout from '../AdminLayout';
import { motion } from 'framer-motion';
import apiManager from '../../../../lib/api';
import { CustomerAnalytics } from '../../../../lib/api/services/analytics';

const CustomerAnalyticsPage = () => {
  const [customerData, setCustomerData] = useState<CustomerAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  
  useEffect(() => {
    const fetchCustomerData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Obtener datos reales del backend
        const response = await apiManager.analytics.getCustomerAnalytics({
          period: dateRange
        });
        
        if (response.success && response.data) {
          setCustomerData(response.data);
        } else {
          setError(response.error?.message || 'Error al cargar los datos de clientes');
        }
      } catch (err) {
        console.error('Error al cargar datos de clientes:', err);
        setError('Error al cargar los datos. Inténtalo de nuevo más tarde.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCustomerData();
  }, [dateRange]);
  
  return (
    <AdminLayout title="Análisis de Clientes">
      {/* Selector de rango de fechas */}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Comportamiento de Clientes</h2>
        <div className="flex bg-white rounded-md shadow-sm">
          <button
            onClick={() => setDateRange('week')}
            className={`px-4 py-2 text-sm rounded-l-md ${
              dateRange === 'week' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Semana
          </button>
          <button
            onClick={() => setDateRange('month')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'month' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Mes
          </button>
          <button
            onClick={() => setDateRange('quarter')}
            className={`px-4 py-2 text-sm ${
              dateRange === 'quarter' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Trimestre
          </button>
          <button
            onClick={() => setDateRange('year')}
            className={`px-4 py-2 text-sm rounded-r-md ${
              dateRange === 'year' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            Año
          </button>
        </div>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          <p>{error}</p>
        </div>
      ) : customerData ? (
        <div className="space-y-6">
          {/* Segmentación de clientes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Segmentación de Clientes</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {customerData.customerGroups.map((group, index) => (
                <motion.div 
                  key={group.id}
                  className="bg-gray-50 p-4 rounded-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <h4 className="font-medium text-gray-800">{group.segment}</h4>
                  <div className="mt-2 flex flex-col">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500 text-sm">Total</span>
                      <span className="font-semibold">{group.count} usuarios</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-gray-500 text-sm">Porcentaje</span>
                      <span className="font-semibold">{group.percentage}%</span>
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-gray-500 text-sm">Valor medio</span>
                      <span className="font-semibold">{group.avgOrderValue.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Canales de adquisición */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Canales de Adquisición</h3>
            <div className="space-y-4">
              {customerData.acquisitionChannels.map((channel, index) => (
                <motion.div 
                  key={channel.channel}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="flex justify-between items-center mb-1">
                    <div>
                      <span className="font-medium text-gray-700">{channel.channel}</span>
                      <span className="ml-2 text-sm text-gray-500">{channel.customers} clientes</span>
                    </div>
                    <span className="text-sm font-semibold">{channel.percentage}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2.5">
                    <div 
                      className="bg-indigo-600 h-2.5 rounded-full" 
                      style={{ width: `${channel.percentage}%` }}
                    ></div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          
          {/* Retención de clientes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Retención de Clientes</h3>
              <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full font-medium">
                Tasa global: {customerData.retention.rate}%
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Mes
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nuevos Clientes
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Clientes Recurrentes
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tasa de Retención
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerData.retention.byMonth.map((month, index) => (
                    <motion.tr 
                      key={month.month}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {month.month}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {month.newCustomers}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {month.returningCustomers}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          month.rate >= 70 ? 'bg-green-100 text-green-800' : 
                          month.rate >= 50 ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-red-100 text-red-800'
                        }`}>
                          {month.rate}%
                        </span>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Mejores clientes */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Mejores Clientes</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pedidos
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gasto Total
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Último Pedido
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {customerData.topCustomers.map((customer, index) => (
                    <motion.tr 
                      key={customer.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {customer.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.orders}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {customer.totalSpent.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(customer.lastOrder).toLocaleDateString('es-ES')}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <p className="text-gray-500">No hay datos disponibles</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default CustomerAnalyticsPage; 